// Mood Selector Web Component

class MoodSelector extends HTMLElement {
  constructor() {
    super();
    this.selectedMood = null;
    this.moodStatus = '';
    this.postText = '';

    // Define mood icons - 5 standard moods + extra shuffle options
    this.standardMoods = [
      { id: 'happy', icon: '😊', label: 'Happy' },
      { id: 'excited', icon: '🤩', label: 'Excited' },
      { id: 'chill', icon: '😎', label: 'Chill' },
      { id: 'thoughtful', icon: '🤔', label: 'Thoughtful' },
      { id: 'grateful', icon: '🙏', label: 'Grateful' }
    ];

    this.shuffleMoods = [
      { id: 'love', icon: '😍', label: 'In Love' },
      { id: 'silly', icon: '🤪', label: 'Silly' },
      { id: 'sleepy', icon: '😴', label: 'Sleepy' },
      { id: 'hungry', icon: '🤤', label: 'Hungry' },
      { id: 'creative', icon: '🎨', label: 'Creative' },
      { id: 'adventurous', icon: '🏔️', label: 'Adventurous' },
      { id: 'cozy', icon: '☕', label: 'Cozy' },
      { id: 'motivated', icon: '💪', label: 'Motivated' },
      { id: 'peaceful', icon: '🧘', label: 'Peaceful' },
      { id: 'curious', icon: '🔍', label: 'Curious' },
      { id: 'festive', icon: '🎉', label: 'Festive' },
      { id: 'nostalgic', icon: '📸', label: 'Nostalgic' }
    ];
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  render() {
    this.innerHTML = `
      <div class="mood-selector">
        <div class="mood-header">
          <post-type-selector selected-type="mood" id="moodPostTypeSelector"></post-type-selector>
        </div>
        
        <div class="mood-icons">
          ${this.standardMoods.map(mood => `
            <button class="mood-icon ${this.selectedMood?.id === mood.id ? 'selected' : ''}" 
                    data-mood-id="${mood.id}" 
                    data-mood-icon="${mood.icon}" 
                    data-mood-label="${mood.label}"
                    title="${mood.label}">
              ${mood.icon}
            </button>
          `).join('')}
          
          <!-- Shuffle button (washing machine) -->
          <button class="mood-icon shuffle-btn" id="shuffleBtn" title="Shuffle for more moods">
            🌀
          </button>
        </div>
        
        <div class="mood-status-input" style="display: ${this.selectedMood ? 'block' : 'none'};">
          <textarea 
            id="moodStatusText"
            placeholder="What's happening? (optional)"
            maxlength="200"
            rows="2"
          >${this.moodStatus}</textarea>
          <div class="char-count">
            <span id="charCount">${this.moodStatus.length}</span>/200
          </div>
        </div>
        
        <div class="mood-post-text" style="display: ${this.selectedMood ? 'block' : 'none'};">
          <textarea 
            id="moodPostText"
            placeholder="Add your thoughts... (optional)"
            rows="3"
          >${this.postText}</textarea>
        </div>
        
        <div class="mood-actions" style="display: ${this.selectedMood ? 'flex' : 'none'};">
          <button class="clear-mood-btn" id="clearMoodBtn">Clear</button>
          <button class="share-mood-btn" id="shareMoodBtn">Share Mood</button>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Post type selector
    const postTypeSelector = this.querySelector('#moodPostTypeSelector');
    if (postTypeSelector) {
      postTypeSelector.addEventListener('post-type-selected', (e) => {
        // Dispatch event to parent post-writer to switch types
        this.dispatchEvent(new CustomEvent('switch-post-type', {
          detail: { type: e.detail.type },
          bubbles: true
        }));
      });
    }

    // Mood icon selection
    this.querySelectorAll('.mood-icon:not(.shuffle-btn)').forEach(icon => {
      icon.addEventListener('click', (e) => {
        const moodId = e.target.dataset.moodId;
        const moodIcon = e.target.dataset.moodIcon;
        const moodLabel = e.target.dataset.moodLabel;

        this.selectMood({ id: moodId, icon: moodIcon, label: moodLabel });
      });
    });

    // Shuffle button
    const shuffleBtn = this.querySelector('#shuffleBtn');
    shuffleBtn.addEventListener('click', () => {
      this.shuffleMoodIcons();
    });

    // Status text input
    const statusText = this.querySelector('#moodStatusText');
    statusText.addEventListener('input', (e) => {
      this.moodStatus = e.target.value;
      this.updateCharCount();
    });

    // Post text input
    const postTextArea = this.querySelector('#moodPostText');
    if (postTextArea) {
      postTextArea.addEventListener('input', (e) => {
        this.postText = e.target.value;
      });
    }

    // Clear button
    const clearBtn = this.querySelector('#clearMoodBtn');
    clearBtn.addEventListener('click', () => {
      this.clearMood();
    });

    // Share button
    const shareBtn = this.querySelector('#shareMoodBtn');
    shareBtn.addEventListener('click', () => {
      this.shareMood();
    });
  }

  selectMood(mood) {
    this.selectedMood = mood;
    this.render();
    this.attachEventListeners();

    // Focus the status input
    setTimeout(() => {
      const statusInput = this.querySelector('#moodStatusText');
      if (statusInput) {
        statusInput.focus();
      }
    }, 100);
  }

  shuffleMoodIcons() {
    // Get random moods from shuffle pool
    const shuffled = [...this.shuffleMoods].sort(() => Math.random() - 0.5);
    const newMoods = shuffled.slice(0, 5);

    // Replace standard moods temporarily
    const originalMoods = [...this.standardMoods];
    this.standardMoods = newMoods;

    this.render();
    this.attachEventListeners();

    // Restore original moods after 10 seconds
    setTimeout(() => {
      this.standardMoods = originalMoods;
      this.render();
      this.attachEventListeners();
    }, 10000);
  }

  clearMood() {
    this.selectedMood = null;
    this.moodStatus = '';
    this.postText = '';
    this.render();
    this.attachEventListeners();
  }

  updateCharCount() {
    const charCount = this.querySelector('#charCount');
    if (charCount) {
      charCount.textContent = this.moodStatus.length;

      charCount.classList.remove('char-count-ok', 'char-count-warning', 'char-count-error');
      if (this.moodStatus.length > 180) {
        charCount.classList.add('char-count-error');
      } else if (this.moodStatus.length > 150) {
        charCount.classList.add('char-count-warning');
      } else {
        charCount.classList.add('char-count-ok');
      }
    }
  }

  async shareMood() {
    if (!this.selectedMood) return;

    try {
      const shareBtn = this.querySelector('#shareMoodBtn');
      shareBtn.disabled = true;
      shareBtn.textContent = 'Sharing...';

      // Create mood post
      const postContent = this.postText.trim() || `Feeling ${this.selectedMood.label.toLowerCase()}${this.moodStatus ? ` - ${this.moodStatus}` : ''}`;
      const postData = {
        post_type: 'mood',
        mood_icon: this.selectedMood.icon,
        title: `${this.selectedMood.label} Mood`,
        content: postContent
      };

      // Only include mood_status if it has a value
      const trimmedStatus = this.moodStatus.trim();
      if (trimmedStatus) {
        postData.mood_status = trimmedStatus;
      }

      Object.assign(postData, this.closest('post-writer')?.getPostStylePayload?.() || {});

      const post = await window.API.posts.create(postData);

      // Clear the mood selector
      this.clearMood();

      // Dispatch event to notify other components
      this.dispatchEvent(new CustomEvent('mood-shared', {
        detail: { moodPost: post },
        bubbles: true
      }));

      // Show success feedback
      this.showFeedback('Mood shared! 🌈', 'success');

    } catch (error) {
      window.logger.error('Error sharing mood:', error);
      const message = error.message || 'Failed to share mood. Please try again.';
      const stylePicker = this.closest('post-writer')?.querySelector('#postStylePicker');
      if (stylePicker?.getStyle?.() && stylePicker.showError) {
        stylePicker.showError(message);
      }
      this.showFeedback(message, 'error');
    } finally {
      // Re-enable the share button
      const shareBtn = this.querySelector('#shareMoodBtn');
      if (shareBtn) {
        shareBtn.disabled = false;
        shareBtn.textContent = 'Share Mood';
      }
    }
  }

  showFeedback(message, type = 'info') {
    // Create temporary feedback element
    const feedback = document.createElement('div');
    feedback.className = `mood-feedback ${type}`;
    feedback.textContent = message;
    feedback.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 0.75rem 1rem;
      border-radius: 6px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease;
      background: ${type === 'success' ? 'var(--success-color)' : type === 'error' ? 'var(--error-color)' : 'var(--info-color)'};
    `;

    document.body.appendChild(feedback);

    // Remove after 3 seconds
    setTimeout(() => {
      feedback.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => feedback.remove(), 300);
    }, 3000);
  }

  // Public method to get current mood data
  getMoodData() {
    return {
      selectedMood: this.selectedMood,
      moodStatus: this.moodStatus
    };
  }

  // Public method to reset the selector
  reset() {
    this.clearMood();
  }

  // Refresh the posts feed
  refreshPostsFeed() {
    // Find and refresh the posts feed component
    const postsFeed = document.querySelector('posts-feed');
    if (postsFeed && postsFeed.load) {
      postsFeed.load();
    }

    // Also refresh activity panel if it exists
    const activityPanel = document.querySelector('activity-panel');
    if (activityPanel && activityPanel.loadFriendActivities) {
      activityPanel.loadFriendActivities();
    }
  }
}

// Register the component
customElements.define('mood-selector', MoodSelector);

export default MoodSelector;
